import { useEffect, useState } from "react";
import axios from "axios";
import { BACKEND_URL } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { StarRating, StarInput } from "./StarRating";
import {
  ChatCircleText, Sparkle, PaperPlaneTilt, ThumbsUp, ShieldCheck, ArrowBendDownRight,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { customerApi, useCustomerAuth } from "../../lib/customerAuth";

const pub = axios.create({ baseURL: `${BACKEND_URL}/api`, withCredentials: true });

/**
 * Reviews section for a public org profile.
 * - Lista reseñas aprobadas
 * - Botón "Deja tu opinión" abre modal con stars + comment
 */
/**
 * ReviewsSection: si la URL trae `?review=1` se auto-abre el modal
 * con datos pre-rellenados desde query params (plate, name).
 */
export default function ReviewsSection({ slug, orgName }) {
  const { account } = useCustomerAuth();
  const [data, setData] = useState({ rating_avg: 0, rating_count: 0, items: [] });
  // F2 · Auto-abrir modal si la URL trae ?review=1 (email link post-servicio)
  const initialQs = (() => {
    if (typeof window === "undefined") return { auto: false, plate: "", name: "" };
    const qs = new URLSearchParams(window.location.search);
    return {
      auto: qs.get("review") === "1",
      plate: (qs.get("plate") || "").toUpperCase(),
      name: qs.get("name") || "",
    };
  })();
  const [open, setOpen] = useState(initialQs.auto);
  const [stars, setStars] = useState(5);
  const [form, setForm] = useState({
    customer_name: initialQs.name,
    customer_email: "",
    comment: "",
    license_plate: initialQs.plate,
  });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      // F3 · usar endpoint con meta (likes_count, is_liked, verified, owner_reply)
      const { data: d } = await pub.get(`/public/reviews-with-meta/${slug}`);
      setData(d);
    } catch (err) {
      // Fallback legacy
      try {
        const { data: d } = await pub.get(`/public/reviews/${slug}`);
        setData(d);
      } catch (e) {
        console.warn("ReviewsSection.load failed", e);
      }
    }
  };
  useEffect(() => { if (slug) load(); /* eslint-disable-next-line */ }, [slug]);

  const submit = async () => {
    if (!form.customer_name.trim()) { toast.error("Tu nombre es obligatorio"); return; }
    if (!stars) { toast.error("Selecciona estrellas"); return; }
    setBusy(true);
    try {
      await pub.post("/public/reviews", { org_slug: slug, stars, ...form });
      toast.success("¡Gracias por tu opinión!");
      setOpen(false);
      setForm({ customer_name: "", customer_email: "", comment: "", license_plate: "" });
      setStars(5);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo enviar la reseña");
    } finally { setBusy(false); }
  };

  // F2 · Distribución de estrellas
  const distribution = [5, 4, 3, 2, 1].map((s) => ({
    stars: s,
    count: (data.items || []).filter((r) => Math.round(r.stars) === s).length,
  }));
  const totalForBars = Math.max(1, data.rating_count || data.items.length);

  return (
    <section className="bg-[var(--ag-midnight-soft)] border border-[var(--ag-border)] rounded-md p-6" data-testid="reviews-section">
      <div className="grid sm:grid-cols-[auto_1fr_auto] gap-5 items-start mb-6">
        {/* Big rating */}
        <div className="text-center sm:text-left">
          <div className="text-[10px] uppercase tracking-[0.25em] text-[var(--ag-text-dim)] flex items-center gap-2 justify-center sm:justify-start">
            <ChatCircleText weight="duotone" /> Opiniones
          </div>
          <div className="font-display font-black text-6xl tabular-nums text-[var(--ag-gold)] leading-none mt-2" data-testid="reviews-avg">
            {data.rating_avg > 0 ? data.rating_avg.toFixed(1) : "—"}
          </div>
          <div className="flex justify-center sm:justify-start mt-2">
            <StarRating value={data.rating_avg} count={data.rating_count} size={16} showCount={false} />
          </div>
          <div className="text-xs text-[var(--ag-text-dim)] tabular-nums mt-1" data-testid="reviews-count">
            {data.rating_count} {data.rating_count === 1 ? "reseña" : "reseñas"}
          </div>
        </div>

        {/* F2 · Distribución de estrellas */}
        <div className="space-y-1.5 min-w-0 sm:px-4" data-testid="reviews-distribution">
          {distribution.map(({ stars, count }) => {
            const pct = (count / totalForBars) * 100;
            return (
              <div key={stars} className="flex items-center gap-2.5 text-xs">
                <span className="w-6 tabular-nums text-right text-[var(--ag-text-dim)]">{stars}★</span>
                <div className="flex-1 h-2 rounded-full overflow-hidden bg-white/5">
                  <div
                    className="h-full rounded-full transition-all"
                    style={{
                      width: `${pct}%`,
                      background: pct > 0 ? "linear-gradient(90deg, var(--ag-gold), var(--ag-gold-soft))" : "transparent",
                    }}
                  />
                </div>
                <span className="w-8 tabular-nums text-right text-[var(--ag-text-dim)]">{count}</span>
              </div>
            );
          })}
        </div>

        {/* CTA escribir reseña */}
        <Button
          onClick={() => setOpen(true)}
          className="ag-btn-gold rounded-xl h-11 px-5 shadow-lg shadow-amber-900/20"
          data-testid="reviews-open-form"
        >
          <Sparkle size={14} weight="fill" className="mr-1.5" /> Escribir reseña
        </Button>
      </div>

      {data.items.length === 0 ? (
        <div className="text-center py-10 border border-dashed border-[var(--ag-border)] rounded-md">
          <ChatCircleText size={36} weight="duotone" className="text-[var(--ag-text-dim)] mx-auto" />
          <p className="text-sm text-[var(--ag-text-dim)] mt-3">Sé el primero en valorar este centro</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {data.items.map((r) => (
            <ReviewCard key={r.id} review={r} account={account} onChange={load} />
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm max-w-md" data-testid="review-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl tracking-tighter">Tu opinión sobre {orgName}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="text-center py-2">
              <Label className="text-xs uppercase tracking-wider text-[var(--ag-text-dim)]">¿Cómo valoras tu experiencia?</Label>
              <div className="mt-2 flex justify-center">
                <StarInput value={stars} onChange={setStars} size={32} testId="review-stars" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider">Nombre *</Label>
                <Input value={form.customer_name} onChange={(e)=>setForm({...form, customer_name: e.target.value})} className="mt-1 rounded-sm" data-testid="review-name" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Matrícula</Label>
                <Input value={form.license_plate} onChange={(e)=>setForm({...form, license_plate: e.target.value.toUpperCase()})} className="mt-1 rounded-sm font-mono-tab" />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Email</Label>
              <Input type="email" value={form.customer_email} onChange={(e)=>setForm({...form, customer_email: e.target.value})} className="mt-1 rounded-sm" data-testid="review-email" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Comentario</Label>
              <Textarea value={form.comment} onChange={(e)=>setForm({...form, comment: e.target.value})} className="mt-1 rounded-sm" rows={4} maxLength={1000} placeholder="¿Qué destacarías del servicio?" data-testid="review-comment" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setOpen(false)} className="rounded-sm">Cancelar</Button>
            <Button onClick={submit} disabled={busy || !form.customer_name} className="ag-btn-gold" data-testid="review-submit">
              {busy ? "Enviando…" : (<><PaperPlaneTilt size={14} className="mr-1.5" /> Enviar opinión</>)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}

/**
 * F3 · Card individual de reseña con verified-badge, likes y owner_reply.
 */
function ReviewCard({ review, account, onChange }) {
  const [likes, setLikes] = useState(review.likes_count || 0);
  const [isLiked, setIsLiked] = useState(!!review.is_liked);
  const [busy, setBusy] = useState(false);

  const toggleLike = async () => {
    if (!account) {
      toast.info("Inicia sesión para dar like a una reseña");
      return;
    }
    setBusy(true);
    try {
      const { data } = await customerApi.post(`/public/reviews/${review.id}/like`);
      setIsLiked(data.liked);
      setLikes(data.likes_count);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo procesar");
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-[var(--ag-midnight)] border border-[var(--ag-border)] rounded-md p-4" data-testid={`review-card-${review.id}`}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#B8860B] grid place-items-center text-[var(--ag-text)] font-display font-black text-sm shrink-0">
            {(review.customer_name || "?").charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0">
            <div className="text-sm font-medium truncate flex items-center gap-1.5">
              {review.customer_name}
              {review.verified && (
                <span
                  className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full bg-sky-500/15 text-sky-300"
                  title="Cita confirmada"
                  data-testid={`review-verified-${review.id}`}
                >
                  <ShieldCheck size={9} weight="fill" /> Verificada
                </span>
              )}
            </div>
            <StarRating value={review.stars} size={11} showCount={false} />
          </div>
        </div>
        <button
          onClick={toggleLike}
          disabled={busy}
          className={`flex items-center gap-1 text-xs px-2.5 py-1 rounded-full transition shrink-0 ${
            isLiked
              ? "bg-[#D4AF37]/15 text-[#D4AF37]"
              : "bg-white/5 text-[var(--ag-text-dim)] hover:bg-white/10"
          }`}
          data-testid={`review-like-${review.id}`}
          aria-label="Me gusta"
        >
          <ThumbsUp size={12} weight={isLiked ? "fill" : "regular"} />
          {likes > 0 && <span className="tabular-nums">{likes}</span>}
        </button>
      </div>
      {review.comment && <p className="text-sm text-[var(--ag-text-dim)] leading-relaxed">{review.comment}</p>}
      <div className="text-[10px] uppercase tracking-wider text-[var(--ag-text-dim)] mt-3">
        {new Date(review.created_at).toLocaleDateString("es-ES", { year: "numeric", month: "short", day: "numeric" })}
      </div>

      {/* Owner reply */}
      {review.owner_reply && (
        <div className="mt-3 pl-3 border-l-2 border-[var(--ag-gold)]/40 bg-white/[0.02] rounded-sm p-2" data-testid={`review-reply-${review.id}`}>
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold text-[var(--ag-gold)] mb-1">
            <ArrowBendDownRight size={10} weight="fill" /> Respuesta del centro
          </div>
          <p className="text-xs text-[var(--ag-text-dim)] leading-relaxed">{review.owner_reply}</p>
        </div>
      )}
    </div>
  );
}

